import React, { FC, useRef, useState, useEffect } from 'react';
import Tree, { TreeNode, ExpandData } from 'rc-tree';
import dotFull from '../../../assets/images/dotFull.png';
import dotEmpty from '../../../assets/images/dotEmpty.png';
import 'rc-tree/assets/index.css';
import './Tree.scss';
import classNames from 'classnames';
import { blades } from './Helpers';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { Tooltip } from 'antd';
import { MLAssessmentScore } from '../../AnnotationManager/Annotation/annotation.types';
import { getMLScoreContent } from '../../AnnotationManager/Annotation/Helpers';

type TreeViewProps = {
  turbine: string;
  onSelectSide: (blade: string, side: string) => void;
  completedSides: string[];
  disabled: boolean;
  showAdditional: boolean;
  sequenceAdditionalData?: Array<{ bladePath: string; additional: string; haveQC: boolean; mlScore?: MLAssessmentScore | undefined }> | null;
  preCheckedKeys?: string[] | null;
  selectedBlade: string;
  selectedSide: string;
};

const STTreeView: FC<TreeViewProps> = ({
  turbine,
  onSelectSide,
  completedSides,
  preCheckedKeys,
  disabled,
  sequenceAdditionalData,
  showAdditional,
  selectedBlade,
  selectedSide
}) => {
  const nodeTurbine = useRef<any>(null);
  const nodeA = useRef<any>(null);
  const nodeB = useRef<any>(null);
  const nodeC = useRef<any>(null);

  const [, forceUpdate] = useState<any>();

  const onSelect = (selectedKeys: Array<string>, e: any) => {
    if (selectedKeys.length === 0 || selectedKeys[0].indexOf('/') === -1) {
      e.node.selectHandle.parentElement.firstChild.click();
      return;
    }

    const newBlade = selectedKeys[0].split('/')[0];
    const newSide = selectedKeys[0].split('/')[1];

    if (newBlade === selectedBlade && newSide === selectedSide) {
      return;
    }

    onSelectSide(newBlade, newSide);
  };

  const onExpand = (expandedKeys: string[], e: ExpandData) => {
    if (!e.node.props.expanded && e.node.props.title === turbine) forceUpdate({});
  };

  useEffect(() => {
    registerOnClickEvents();
  });

  const registerOnClickEvents = () => {
    nodeTurbine.current!.selectHandle.onclick = (e: any) => {
      nodeTurbine.current!.selectHandle.parentElement.firstChild.click();
    };
    nodeA.current!.selectHandle.onclick = (e: any) => {
      nodeA.current!.selectHandle.parentElement.firstChild.click();
    };
    nodeB.current!.selectHandle.onclick = (e: any) => {
      nodeB.current!.selectHandle.parentElement.firstChild.click();
    };
    nodeC.current!.selectHandle.onclick = (e: any) => {
      nodeC.current!.selectHandle.parentElement.firstChild.click();
    };
  };

  const getClassIfSequenceCompleted = (sequenceKey: string) => {
    return completedSides.find(x => x === sequenceKey) ? 'completed-stitching' : '';
  };

  const getClassIfBladeCompleted = (bladeKey: string) => {
    return completedSides.filter(x => x.startsWith(bladeKey)).length === 4 ? 'completed-stitching' : '';
  };

  const getClassIfTurbineCompleted = () => {
    return completedSides.length === 13 ? 'completed-stitching' : '';
  };

  const emptyImage = <img src='' alt='' />;
  const selectedNodeIcon = <img src={dotEmpty} alt='' />;
  const notSelectedNodeIcon = <img src={dotFull} alt='' />;

  const getSequenceAdditionalData = (bladePath: string) => {
    if (!showAdditional) {
      return '';
    }

    if (sequenceAdditionalData && sequenceAdditionalData.length === 0) {
      return '...';
    }

    const sequence = sequenceAdditionalData && sequenceAdditionalData.find(x => x.bladePath === bladePath);

    if (sequence) {
      return (
        <>
          ({sequence.additional}){' '}
          {(sequence.haveQC || (sequence.mlScore && sequence.mlScore < MLAssessmentScore.MajorAdjustmentNeeded)) && (
            <Tooltip placement='left' title={getMLScoreContent(sequence.mlScore)}>
              <FontAwesomeIcon icon={faExclamationTriangle} className='qc-issues' />
            </Tooltip>
          )}
        </>
      );
    }

    return '';
  };
  const getBladeAdditionalData = (position: string) => {
    if (!showAdditional) {
      return '';
    }

    if (sequenceAdditionalData && sequenceAdditionalData.length === 0) {
      return '...';
    }

    let total = 0;
    let totalToBeTransferred = 0;
    sequenceAdditionalData &&
      sequenceAdditionalData.forEach(x => {
        if (x.bladePath.startsWith(`${position}/`)) {
          var additionalParsed = x.additional.split('/');
          totalToBeTransferred += Number(additionalParsed[0]);
          total += Number(additionalParsed[1]);
        }
      });

    return (
      <>
        ({totalToBeTransferred}/{total})
      </>
    );
  };

  const getTurbineAdditionalData = () => {
    if (!showAdditional) {
      return '';
    }

    if (sequenceAdditionalData && sequenceAdditionalData.length === 0) {
      return '...';
    }

    let total = 0;
    let totalToBeTransferred = 0;
    sequenceAdditionalData &&
      sequenceAdditionalData.forEach(x => {
        var additionalParsed = x.additional.split('/');
        totalToBeTransferred += Number(additionalParsed[0]);
        total += Number(additionalParsed[1]);
      });

    return (
      <>
        ({totalToBeTransferred}/{total})
      </>
    );
  };

  const getNodeRef = (position: string) => {
    switch (position) {
      case 'A':
        return nodeA;
      case 'B':
        return nodeB;
      case 'C':
        return nodeC;
      default:
        break;
    }
  };

  return (
    <Tree
      showLine
      defaultExpandAll
      selectedKeys={preCheckedKeys ? preCheckedKeys : []}
      showIcon={true}
      onSelect={onSelect}
      onExpand={onExpand}
      className={classNames({ 'tree-view-disabled': disabled })}
    >
      <TreeNode
        title={
          <span>
            {turbine} {getTurbineAdditionalData()}
          </span>
        }
        key='turbine'
        disabled
        icon={emptyImage}
        ref={nodeTurbine}
        className={getClassIfTurbineCompleted()}
      >
        {blades.map(blade => (
          <TreeNode
            title={
              <span>
                {blade.position} {getBladeAdditionalData(blade.position)}
              </span>
            }
            key={blade.position}
            icon={emptyImage}
            disabled
            ref={getNodeRef(blade.position)}
            className={getClassIfBladeCompleted(blade.position)}
          >
            {blade.sides.map(side => (
              <TreeNode
                title={
                  <span>
                    {side.value} {getSequenceAdditionalData(`${blade.position}/${side.key}`)}
                  </span>
                }
                key={`${blade.position}/${side.key}`}
                icon={preCheckedKeys && preCheckedKeys.includes(`${blade.position}/${side.key}`) ? selectedNodeIcon : notSelectedNodeIcon}
                className={getClassIfSequenceCompleted(`${blade.position}/${side.key}`)}
              />
            ))}
          </TreeNode>
        ))}
      </TreeNode>
    </Tree>
  );
};

export default STTreeView;
